import { Spinner } from "@visiblethread/react-components";
import { USER_ROLES } from "constants/app";
import { SESSION_FREE_ROUTES } from "constants/routes";
import { useWorkspaceSync } from "hooks/generic/useWorkspaceSync";
import { any } from "prop-types";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { getSessionInfo } from "slices/app/actions/app";
import {
  selectApplication,
  selectHasNoSession,
  selectHasWorkspaces,
  selectIsCloudMode,
  selectIsInitialized,
  selectIsLoggedIn,
  selectIsSso,
  selectIsSwitchingWorkspace,
  selectUserRoles,
  selectUserWorkspaceId,
} from "slices/app/selectors";
import { fetchCurrencies } from "slices/currency";
import { loadFeatureFlags } from "slices/featureFlags";
import { hasRole } from "utils";

import useEntityWorkspaceResolution from "./useEntityWorkspaceResolution";

const requiresSession = (pathname) =>
  !SESSION_FREE_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`)
  );

const SessionResolver = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useWorkspaceSync();
  useEntityWorkspaceResolution();

  const application = useSelector(selectApplication);
  const isInitialized = useSelector(selectIsInitialized);
  const hasNoSession = useSelector(selectHasNoSession);

  const isCloudMode = useSelector(selectIsCloudMode);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isSsoAuthType = useSelector(selectIsSso);
  const isSwitchingWorkspace = useSelector(selectIsSwitchingWorkspace);
  const roles = useSelector(selectUserRoles);
  const workspaceId = useSelector(selectUserWorkspaceId);
  const hasWorkspaces = useSelector(selectHasWorkspaces);

  const isFeatureFlagOnly =
    hasRole(roles, "feature_flag") && !hasRole(roles, "user") && !hasWorkspaces;
  const isOnPremSsoAdminLogin =
    isCloudMode === false && window.location.hash.includes("/systemAdminLogin");
  const isWaitingForSamlRedirect =
    isInitialized &&
    isSsoAuthType &&
    isLoggedIn === false &&
    !isOnPremSsoAdminLogin;

  useEffect(() => {
    if (hasNoSession) {
      dispatch(getSessionInfo(navigate));
    }
  }, [location.hash, navigate, hasNoSession]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(loadFeatureFlags());
    }
  }, [isLoggedIn, dispatch]);

  // /api/currencies sits under the hasRole("USER") catch-all, so a feature-flag-only or
  // customer-success-only session gets a 403 from it.
  useEffect(() => {
    if (hasRole(roles, USER_ROLES.user)) {
      dispatch(fetchCurrencies());
    }
  }, [roles, workspaceId, dispatch]);

  if (
    !hasNoSession &&
    !window.location.hash.includes("/setup") &&
    application?.licensed === false &&
    isCloudMode === false &&
    !isFeatureFlagOnly
  ) {
    navigate("/setup");
  }

  if (!isInitialized || isSwitchingWorkspace) {
    return <Spinner fullScreen size="62px" />;
  }
  if (isWaitingForSamlRedirect) {
    return null;
  }
  // never show login page for authenticated user ever
  if (isLoggedIn && location.pathname === "/login") {
    return <Navigate to="/" replace />;
  }
  // A session that resolved as logged out is always on its way to /login, but the
  // router's location can still be the one we are leaving when Redux forces this
  // re-render — the two updates travel different paths and Redux's wins. Rendering
  // then mounts whatever matches the stale URL, which on / is the dashboard: it fires
  // its own requests, takes 401s, and bounces the user to #/login/timed-out with no
  // session to have timed out. Hold the spinner until the destination catches up.
  if (isLoggedIn === false && requiresSession(location.pathname)) {
    return <Spinner fullScreen size="62px" />;
  }
  return children;
};

SessionResolver.propTypes = {
  children: any,
};

export default SessionResolver;
